import {
  Outlet,
  useLocation,
  useOutletContext,
  Navigate,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import apiCliente from "../../../services/apiCliente";
import UnauthorizedPage from "../../../components/unauthorizedPage";

export async function checkIsPermissionCliente(route) {
  try {
    const response = await apiCliente.post("/cliente/checkPermissionUser", {
      route: route,
    });

    return response?.data;
  } catch (error) {
    return false;
  }
}

export default function ClientePermissionRoute({ permissionPath, children }) {
  const context = useOutletContext();
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  // Guarda a "rota base" que já foi verificada
  const [verifiedRoutePath, setVerifiedRoutePath] = useState(null);

  // 1. Descobre qual "família" de rota precisa ser validada
  const getPathToVerify = (path) => {
    let p = permissionPath || path;
    if (!p) return p;

    // Modo espelho: as rotas do cliente são montadas também sob /mirror.
    // A permissão é sempre a do usuário do portal espelhado, sem o prefixo.
    p = p.replace(/^\/mirror/, "");

    // Remove barra final para casar com os paths cadastrados
    if (p.length > 1 && p.endsWith("/")) {
      p = p.slice(0, -1);
    }

    return p || "/";
  };

  // Avaliamos a rota base FORA do useEffect
  const currentPathToVerify = getPathToVerify(location.pathname);

  useEffect(() => {
    let isMounted = true;

    const verifyAccess = async () => {
      // 1. Verifica se a navegação está acontecendo dentro do mesmo "módulo pai"
      // Pega os primeiros 3 segmentos da URL (ex: ["", "home", "overview"])
      const basePathAnterior = verifiedRoutePath
        ?.split("/")
        ?.slice(0, 3)
        ?.join("/");
      const basePathAtual = currentPathToVerify
        ?.split("/")
        ?.slice(0, 3)
        ?.join("/");

      const isSameModuleNavigation =
        verifiedRoutePath && basePathAnterior === basePathAtual;

      // 2. Só exibe o loading "tela cheia" se for a primeira vez carregando
      // ou se estiver mudando para um módulo completamente diferente
      if (!isSameModuleNavigation) {
        setStatus("loading");
      }

      try {
        await apiCliente.get("/cliente/authenticationToken");

        const userPermission =
          await checkIsPermissionCliente(currentPathToVerify);

        // Vale para o ADM também: ele não tem lista de rotas (o bypass é um
        // flag), mas o backend devolve routesPermited false quando o interno
        // desabilitou a rota para o portal do cliente
        const hasAccess = !!userPermission?.routesPermited;

        if (isMounted) {
          setStatus(hasAccess ? "authorized" : "unauthorized");
          setVerifiedRoutePath(currentPathToVerify); // Salva a rota base como verificada
        }
      } catch (error) {
        if (isMounted) {
          setStatus("unauthenticated");
          setVerifiedRoutePath(currentPathToVerify);
        }
      }
    };

    // Só executa a requisição se a rota base atual for diferente da última verificada
    if (verifiedRoutePath !== currentPathToVerify) {
      verifyAccess();
    }

    return () => {
      isMounted = false;
    };
  }, [currentPathToVerify, permissionPath]);

  // 2. Redirecionar se não autenticado
  if (status === "unauthenticated") {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }

  // 3. Mostrar página de acesso negado
  if (status === "unauthorized") {
    return <UnauthorizedPage />;
  }

  // 4. Se não estiver autorizado ainda, NÃO exibimos o conteúdo.
  // MAS mantemos ele renderizado (com display: none) para não perder o estado.
  const isAuthorized = status === "authorized";
  const isLoading = status === "loading";

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress size={100} thickness={2.5} />
        </Box>
      )}

      {/* Renderizamos o Outlet sempre, mas escondemos se não estiver autorizado.
          Isso evita que o componente seja destruído e perca dados/faça re-fetch */}
      <Box sx={{ display: isAuthorized ? "block" : "none" }}>
        {children ? children : <Outlet context={context} />}
      </Box>
    </>
  );
}
